/**
 * Helper utilities for working with TrainingPlan's weeks structure
 */
import type { TrainingPlan, TrainingWeek, Workout } from '@shared/types';

/**
 * Get all workouts from a training plan as a flat array
 */
export function getAllWorkouts(plan: TrainingPlan | null | undefined): Workout[] {
  if (!plan?.weeks) return [];
  return plan.weeks.flatMap(week => week.workouts || []);
}

/**
 * Find a workout by date in the training plan
 */
export function findWorkoutByDate(
  plan: TrainingPlan | null | undefined,
  date: string
): { weekIndex: number; workoutIndex: number; workout: Workout } | null {
  if (!plan?.weeks) return null;

  const dateOnly = date.split('T')[0];

  for (let weekIndex = 0; weekIndex < plan.weeks.length; weekIndex++) {
    const week = plan.weeks[weekIndex];
    if (!week.workouts) continue;

    for (let workoutIndex = 0; workoutIndex < week.workouts.length; workoutIndex++) {
      const workout = week.workouts[workoutIndex];
      if (workout.training_date.split('T')[0] === dateOnly) {
        return { weekIndex, workoutIndex, workout };
      }
    }
  }

  return null;
}

/**
 * Calculate total planned distance from all workouts
 */
export function getTotalPlannedDistance(plan: TrainingPlan | null | undefined): number {
  const workouts = getAllWorkouts(plan);
  return workouts.reduce((sum, w) => sum + (w.total_distance || 0), 0);
}

/**
 * Calculate total TSS from all workouts
 */
export function getTotalTSS(plan: TrainingPlan | null | undefined): number {
  const workouts = getAllWorkouts(plan);
  return workouts.reduce((sum, w) => sum + (w.TSS || 0), 0);
}

/**
 * Get workouts for a specific date range
 */
export function getWorkoutsInRange(
  plan: TrainingPlan | null | undefined,
  startDate: string,
  endDate: string
): Workout[] {
  const workouts = getAllWorkouts(plan);
  return workouts.filter(w => {
    const date = w.training_date.split('T')[0];
    return date >= startDate && date <= endDate;
  });
}

/**
 * Get week by date
 */
export function getWeekByDate(
  plan: TrainingPlan | null | undefined,
  date: string
): TrainingWeek | null {
  if (!plan?.weeks) return null;

  const dateOnly = date.split('T')[0];

  return (
    plan.weeks.find(week => {
      return week.workouts?.some(w => w.training_date.split('T')[0] === dateOnly);
    }) || null
  );
}

/**
 * Check if plan has any workouts
 */
export function hasWorkouts(plan: TrainingPlan | null | undefined): boolean {
  return getAllWorkouts(plan).length > 0;
}
